import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

const ONE_MIN = 60 * 1000;

// =====================================================================
// KPIs Caisse Groupe (header dashboard)
// =====================================================================
export interface CaisseKpisGroupe {
  solde_groupe_now: number;
  entrees_30j: number;
  sorties_30j: number;
  tresor_projete_30j: number;
  tresor_projete_90j: number;
  nb_operations_30j: number;
}

export function useCaisseKpisGroupe() {
  return useQuery({
    queryKey: ['caisse_kpis_groupe'],
    queryFn: async (): Promise<CaisseKpisGroupe | null> => {
      const { data, error } = await supabase.from('v_caisse_kpis_groupe').select('*').maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const r = data as Record<string, unknown>;
      return {
        solde_groupe_now: Number(r.solde_groupe_now ?? 0),
        entrees_30j: Number(r.entrees_30j ?? 0),
        sorties_30j: Number(r.sorties_30j ?? 0),
        tresor_projete_30j: Number(r.tresor_projete_30j ?? 0),
        tresor_projete_90j: Number(r.tresor_projete_90j ?? 0),
        nb_operations_30j: Number(r.nb_operations_30j ?? 0),
      };
    },
    staleTime: ONE_MIN,
  });
}

// =====================================================================
// Échéancier par activité
// =====================================================================
export interface EcheancierActivite {
  activite_id: string;
  activite_nom: string;
  activite_code: string;
  a_encaisser_30j: number;
  a_payer_30j: number;
  charges_recurrentes_30j: number;
  avances_a_recevoir: number;
  avances_a_rembourser: number;
  solde_courant: number;
}

export function useEcheancierParActivite() {
  return useQuery({
    queryKey: ['echeancier_par_activite'],
    queryFn: async (): Promise<EcheancierActivite[]> => {
      const { data, error } = await supabase
        .from('v_echeancier_par_activite')
        .select('*')
        .order('activite_nom');
      if (error) throw error;
      return (data ?? []) as unknown as EcheancierActivite[];
    },
    staleTime: 2 * ONE_MIN,
  });
}

// =====================================================================
// Avances inter-activités
// =====================================================================
export type AvanceStatut = 'a_rembourser' | 'rembourse_partiel' | 'rembourse' | 'annule';

export interface AvanceInterActivite {
  id: string;
  reference: string;
  date_avance: string;
  activite_emettrice_id: string;
  activite_beneficiaire_id: string;
  operation_id: string | null;
  montant: number;
  motif: string | null;
  statut: AvanceStatut;
  date_remboursement_prevue: string | null;
  date_remboursement_effective: string | null;
  remboursement_operation_id: string | null;
  montant_rembourse: number;
  observation: string | null;
  created_at: string;
}

export function useAvancesInterActivites(filter: { statut?: AvanceStatut | null } = {}) {
  return useQuery({
    queryKey: ['avances_inter_activites', filter],
    queryFn: async (): Promise<AvanceInterActivite[]> => {
      let q = supabase
        .from('avances_inter_activites')
        .select('*')
        .order('date_avance', { ascending: false });
      if (filter.statut) q = q.eq('statut', filter.statut);
      const { data, error } = await q.limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as AvanceInterActivite[];
    },
  });
}

export function useUpdateAvanceStatut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      statut: AvanceStatut;
      montant_rembourse?: number;
      remboursement_operation_id?: string | null;
    }): Promise<void> => {
      const patch: Record<string, unknown> = { statut: input.statut };
      if (input.montant_rembourse != null) patch.montant_rembourse = input.montant_rembourse;
      if (input.statut === 'rembourse')
        patch.date_remboursement_effective = new Date().toISOString().slice(0, 10);
      if (input.remboursement_operation_id !== undefined)
        patch.remboursement_operation_id = input.remboursement_operation_id;
      const { error } = await supabase
        .from('avances_inter_activites')
        .update(patch as never)
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['avances_inter_activites'] });
      void qc.invalidateQueries({ queryKey: ['echeancier_par_activite'] });
    },
  });
}

// =====================================================================
// Charges récurrentes
// =====================================================================
export type ChargePeriodicite = 'mensuelle' | 'trimestrielle' | 'semestrielle' | 'annuelle';
export type ChargeAllocationType = 'groupe' | 'par_activite' | 'custom';

export interface ChargeRecurrente {
  id: string;
  libelle: string;
  description: string | null;
  activite_id: string | null;
  allocation_type: ChargeAllocationType;
  allocation_pct: Record<string, number> | null;
  montant: number;
  periodicite: ChargePeriodicite;
  jour_du_mois: number | null;
  mois_offset: number;
  prochaine_echeance: string | null;
  auto_generer_echeance: boolean;
  compte_bancaire_id: string | null;
  fournisseur_tier_id: string | null;
  actif: boolean;
  created_at: string;
}

export function useChargesRecurrentes(filter: { actif?: boolean } = {}) {
  return useQuery({
    queryKey: ['charges_recurrentes', filter],
    queryFn: async (): Promise<ChargeRecurrente[]> => {
      let q = supabase
        .from('charges_recurrentes')
        .select('*')
        .order('prochaine_echeance', { ascending: true, nullsFirst: false });
      if (filter.actif != null) q = q.eq('actif', filter.actif);
      const { data, error } = await q.limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as ChargeRecurrente[];
    },
  });
}

export function useUpsertChargeRecurrente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<ChargeRecurrente> & {
        libelle: string;
        montant: number;
        periodicite: ChargePeriodicite;
        allocation_type: ChargeAllocationType;
      },
    ): Promise<ChargeRecurrente> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('charges_recurrentes')
          .update(rest as never)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as ChargeRecurrente;
      }
      const { data, error } = await supabase
        .from('charges_recurrentes')
        .insert(rest as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as ChargeRecurrente;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['charges_recurrentes'] });
      void qc.invalidateQueries({ queryKey: ['echeancier_par_activite'] });
    },
  });
}

export function useDeleteChargeRecurrente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('charges_recurrentes').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['charges_recurrentes'] }),
  });
}

// =====================================================================
// Top 5 risques cash-flow dynamique (Sprint 6.8)
// =====================================================================
export interface RisqueCashFlow {
  type: 'echeance' | 'charge_recurrente' | 'avance';
  id: string;
  libelle: string;
  montant: number;
  date: string;
  jours_restants: number;
  score: number;
  link: string;
}

export function useTopRisquesCashFlow(limit = 5) {
  return useQuery({
    queryKey: ['top_risques_cashflow', limit],
    queryFn: async (): Promise<RisqueCashFlow[]> => {
      const today = new Date();
      const risques: RisqueCashFlow[] = [];

      // 1. Échéances a_payer 30j à venir
      const { data: ech } = await supabase
        .from('echeances')
        .select('id, motif, montant_du, date_echeance, sens, statut')
        .eq('sens', 'a_payer')
        .in('statut', ['ouverte', 'partiellement_payee', 'en_retard', 'mise_en_demeure'])
        .lte('date_echeance', new Date(today.getTime() + 30 * 86400000).toISOString().slice(0, 10))
        .order('date_echeance', { ascending: true })
        .limit(20);
      for (const e of (ech ?? []) as Array<{
        id: string;
        motif: string | null;
        montant_du: number | null;
        date_echeance: string;
        statut: string;
      }>) {
        const j = Math.ceil((new Date(e.date_echeance).getTime() - today.getTime()) / 86400000);
        let score = 0;
        const m = Number(e.montant_du ?? 0);
        if (m > 10_000_000) score += 30;
        else if (m > 1_000_000) score += 15;
        if (j < 7) score += 40;
        else if (j < 15) score += 20;
        if (e.statut === 'en_retard' || e.statut === 'mise_en_demeure') score += 30;
        risques.push({
          type: 'echeance',
          id: e.id,
          libelle: e.motif ?? 'Échéance fournisseur',
          montant: m,
          date: e.date_echeance,
          jours_restants: j,
          score,
          link: '/echeances/a-payer',
        });
      }

      // 2. Charges récurrentes 30j
      const { data: charges } = await supabase
        .from('charges_recurrentes')
        .select('id, libelle, montant, prochaine_echeance')
        .eq('actif', true)
        .lte(
          'prochaine_echeance',
          new Date(today.getTime() + 30 * 86400000).toISOString().slice(0, 10),
        )
        .order('prochaine_echeance', { ascending: true });
      for (const c of (charges ?? []) as Array<{
        id: string;
        libelle: string;
        montant: number;
        prochaine_echeance: string | null;
      }>) {
        if (!c.prochaine_echeance) continue;
        const j = Math.ceil(
          (new Date(c.prochaine_echeance).getTime() - today.getTime()) / 86400000,
        );
        let score = 50; // type charge récurrente = critique
        const m = Number(c.montant);
        if (m > 10_000_000) score += 30;
        else if (m > 1_000_000) score += 15;
        if (j < 7) score += 40;
        else if (j < 15) score += 20;
        if (/loyer|salaire/i.test(c.libelle)) score += 10;
        risques.push({
          type: 'charge_recurrente',
          id: c.id,
          libelle: c.libelle,
          montant: m,
          date: c.prochaine_echeance,
          jours_restants: j,
          score,
          link: '/finances/caisse/charges-recurrentes',
        });
      }

      // 3. Avances inter-activités en retard (> 30j sans remboursement)
      const { data: avances } = await supabase
        .from('avances_inter_activites')
        .select('id, reference, motif, montant, montant_rembourse, date_avance, statut')
        .in('statut', ['a_rembourser', 'rembourse_partiel'])
        .order('date_avance', { ascending: true });
      for (const a of (avances ?? []) as Array<{
        id: string;
        reference: string;
        motif: string | null;
        montant: number;
        montant_rembourse: number;
        date_avance: string;
        statut: string;
      }>) {
        const ageJours = Math.floor(
          (today.getTime() - new Date(a.date_avance).getTime()) / 86400000,
        );
        if (ageJours < 7) continue; // ignore les très récentes
        let score = 30 + Math.min(ageJours, 60);
        const restant = Number(a.montant) - Number(a.montant_rembourse);
        if (restant > 1_000_000) score += 20;
        risques.push({
          type: 'avance',
          id: a.id,
          libelle: `Avance ${a.reference}${a.motif ? ' — ' + a.motif : ''}`,
          montant: restant,
          date: a.date_avance,
          jours_restants: -ageJours,
          score,
          link: '/finances/caisse/avances',
        });
      }

      return risques.sort((a, b) => b.score - a.score).slice(0, limit);
    },
    staleTime: ONE_MIN,
  });
}

// =====================================================================
// Évolution solde 30j (Sprint 6.8)
// =====================================================================
export interface SoldeJour {
  date: string;
  jour: string;
  solde: number;
  variation: number;
  evenement?: string;
}

export function useEvolutionSolde30j() {
  return useQuery({
    queryKey: ['evolution_solde_30j'],
    queryFn: async (): Promise<SoldeJour[]> => {
      // Solde de départ = solde groupe maintenant
      const { data: kpi } = await supabase
        .from('v_caisse_kpis_groupe')
        .select('solde_groupe_now')
        .maybeSingle();
      const soldeDepart = Number(
        (kpi as { solde_groupe_now?: number } | null)?.solde_groupe_now ?? 0,
      );

      const today = new Date();
      const horizonStart = today.toISOString().slice(0, 10);
      const horizonEnd = new Date(today.getTime() + 30 * 86400000).toISOString().slice(0, 10);

      // Toutes les échéances 30j à venir
      const { data: ech } = await supabase
        .from('echeances')
        .select('sens, montant_du, date_echeance, statut, motif')
        .gte('date_echeance', horizonStart)
        .lte('date_echeance', horizonEnd)
        .in('statut', ['ouverte', 'partiellement_payee', 'en_retard', 'mise_en_demeure']);

      // Charges récurrentes 30j
      const { data: charges } = await supabase
        .from('charges_recurrentes')
        .select('libelle, montant, prochaine_echeance')
        .eq('actif', true)
        .gte('prochaine_echeance', horizonStart)
        .lte('prochaine_echeance', horizonEnd);

      // Build jour par jour
      const jours: SoldeJour[] = [];
      let cumul = soldeDepart;
      for (let i = 0; i <= 30; i++) {
        const d = new Date(today.getTime() + i * 86400000);
        const dateIso = d.toISOString().slice(0, 10);
        let variationDay = 0;
        let evenementDay = '';
        for (const e of (ech ?? []) as Array<{
          sens: string;
          montant_du: number | null;
          date_echeance: string;
          motif: string | null;
        }>) {
          if (e.date_echeance === dateIso) {
            const m = Number(e.montant_du ?? 0);
            variationDay += e.sens === 'a_encaisser' ? m : -m;
          }
        }
        for (const c of (charges ?? []) as Array<{
          libelle: string;
          montant: number;
          prochaine_echeance: string | null;
        }>) {
          if (c.prochaine_echeance === dateIso) {
            variationDay -= Number(c.montant);
            evenementDay = evenementDay ? evenementDay + ' · ' + c.libelle : c.libelle;
          }
        }
        cumul += variationDay;
        jours.push({
          date: dateIso,
          jour: d.toLocaleDateString('fr-DZ', { day: '2-digit', month: '2-digit' }),
          solde: cumul,
          variation: variationDay,
          evenement: evenementDay || undefined,
        });
      }
      return jours;
    },
    staleTime: 2 * ONE_MIN,
  });
}

// =====================================================================
// Compensation auto avances symétriques (Sprint 6.8)
// =====================================================================
export interface CompensationProposee {
  activite_a_id: string;
  activite_b_id: string;
  montant_compensable: number;
  avances_a_solder: string[];
}

export function usePreviewCompensations() {
  return useQuery({
    queryKey: ['compensations_preview'],
    queryFn: async (): Promise<CompensationProposee[]> => {
      const { data, error } = await supabase
        .from('avances_inter_activites')
        .select(
          'id, activite_emettrice_id, activite_beneficiaire_id, montant, montant_rembourse, statut',
        )
        .in('statut', ['a_rembourser', 'rembourse_partiel']);
      if (error) throw error;
      // Map paire (A,B) avec A < B en string
      const paires = new Map<
        string,
        { aTowardB: number; bTowardA: number; aIds: string[]; bIds: string[] }
      >();
      for (const a of (data ?? []) as Array<{
        id: string;
        activite_emettrice_id: string;
        activite_beneficiaire_id: string;
        montant: number;
        montant_rembourse: number;
      }>) {
        const restant = Number(a.montant) - Number(a.montant_rembourse);
        if (restant <= 0) continue;
        const [k1, k2] = [a.activite_emettrice_id, a.activite_beneficiaire_id].sort();
        const key = `${k1}|${k2}`;
        const cur = paires.get(key) ?? { aTowardB: 0, bTowardA: 0, aIds: [], bIds: [] };
        // a vers b si emettrice = k1
        if (a.activite_emettrice_id === k1) {
          cur.aTowardB += restant;
          cur.aIds.push(a.id);
        } else {
          cur.bTowardA += restant;
          cur.bIds.push(a.id);
        }
        paires.set(key, cur);
      }
      const result: CompensationProposee[] = [];
      for (const [key, v] of paires) {
        const compensable = Math.min(v.aTowardB, v.bTowardA);
        if (compensable <= 0) continue;
        const [a_id, b_id] = key.split('|');
        if (!a_id || !b_id) continue;
        result.push({
          activite_a_id: a_id,
          activite_b_id: b_id,
          montant_compensable: compensable,
          avances_a_solder: [...v.aIds, ...v.bIds],
        });
      }
      return result;
    },
    staleTime: 30_000,
  });
}

export function useCompenserAvances() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (compensations: CompensationProposee[]): Promise<{ count: number }> => {
      const ids = compensations.flatMap((c) => c.avances_a_solder);
      if (ids.length === 0) return { count: 0 };
      const { error } = await supabase
        .from('avances_inter_activites')
        .update({
          statut: 'rembourse',
          date_remboursement_effective: new Date().toISOString().slice(0, 10),
        } as never)
        .in('id', ids);
      if (error) throw error;
      return { count: ids.length };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['avances_inter_activites'] });
      void qc.invalidateQueries({ queryKey: ['compensations_preview'] });
      void qc.invalidateQueries({ queryKey: ['echeancier_par_activite'] });
    },
  });
}

/**
 * Génère 12 échéances futures pour les charges récurrentes actives avec auto_generer_echeance=true.
 * Retourne le nombre d'échéances créées.
 */
export function useGenererEcheances12Mois() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ created: number; errors: string[] }> => {
      const { data: charges, error } = await supabase
        .from('charges_recurrentes')
        .select('*')
        .eq('actif', true)
        .eq('auto_generer_echeance', true);
      if (error) throw error;
      const errors: string[] = [];
      let created = 0;
      const today = new Date();
      const oneMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth() + 1, d.getDate());
      const inc: Record<ChargePeriodicite, number> = {
        mensuelle: 1,
        trimestrielle: 3,
        semestrielle: 6,
        annuelle: 12,
      };
      for (const c of (charges ?? []) as ChargeRecurrente[]) {
        const start = c.prochaine_echeance ? new Date(c.prochaine_echeance) : oneMonth(today);
        const monthsBetween = inc[c.periodicite];
        const occurrences = Math.max(1, Math.floor(12 / monthsBetween));
        for (let i = 0; i < occurrences; i++) {
          const d = new Date(
            start.getFullYear(),
            start.getMonth() + i * monthsBetween,
            start.getDate(),
          );
          const dateEcheance = d.toISOString().slice(0, 10);
          // Crée une échéance a_payer
          const { error: insErr } = await supabase.from('echeances').insert({
            sens: 'a_payer',
            tier_id: c.fournisseur_tier_id,
            activite_id: c.activite_id,
            date_emission: today.toISOString().slice(0, 10),
            date_echeance: dateEcheance,
            montant_initial: c.montant,
            montant_paye: 0,
            montant_du: c.montant,
            montant_initial_dzd: c.montant,
            montant_ttc: c.montant,
            statut: 'ouverte',
            motif: c.libelle,
            observation: c.description,
          } as never);
          if (insErr) errors.push(`${c.libelle} ${dateEcheance}: ${insErr.message}`);
          else created++;
        }
      }
      return { created, errors };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['echeances'] });
      void qc.invalidateQueries({ queryKey: ['echeancier_par_activite'] });
    },
  });
}
